'use es6';

import { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SidebarHeader from 'ContentEditorUI/components/sidebar/SidebarHeader';
import SlidingPanelWrapper from 'ContentEditorUI/components/sidebar/SlidingPanelWrapper';
import FixedPanelWrapper from 'ContentEditorUI/components/sidebar/FixedPanelWrapper';
import { SIDEBAR_PANELS } from 'ContentEditorUI/lib/sidebar/constants';
import BaymaxSidebarTabsContainer from 'ContentEditorUI/components/sidebar/BaymaxSidebarTabsContainer';
import PreloadedIframeProxy from 'ContentEditorUI/components/temp/PreloadedIframeProxytoExtract';
import SidebarIframeStylesManager from 'ContentEditorUI/utils/SidebarIframeStylesManager';
import { hideSidebarIframeSpinner, showSidebarIframeSpinner, getSidebarIframeWrapperElement, hideSidebarIframeWrapperElement } from 'ContentEditorUI/lib/layoutDom';
import { TransitionGroup } from 'react-transition-group';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default class Sidebar extends Component {
  componentDidMount() {
    const {
      activePanel,
      sidebarIframeNeedsLoadingIndicator
    } = this.props;
    this.updateParentContainerClasses();

    // Make sure to clean up the sidebar iframe an skeleton sidebar spinner on mount if
    // a different tab than "Add" is first shown for the sidebar
    if (!sidebarIframeNeedsLoadingIndicator) {
      hideSidebarIframeSpinner();
    }
    if (activePanel !== SIDEBAR_PANELS.addWidget) {
      hideSidebarIframeWrapperElement();
    }
  }
  componentDidUpdate(prevProps) {
    const {
      sidebarIframeNeedsLoadingIndicator,
      activePanel
    } = this.props;
    this.updateParentContainerClasses();
    const panelChangedToAddWidget = prevProps.activePanel !== 'addWidget' && activePanel === 'addWidget';
    if (!prevProps.sidebarIframeNeedsLoadingIndicator && sidebarIframeNeedsLoadingIndicator) {
      showSidebarIframeSpinner();
    } else if ((prevProps.sidebarIframeNeedsLoadingIndicator || panelChangedToAddWidget) && !sidebarIframeNeedsLoadingIndicator) {
      hideSidebarIframeSpinner();
    }
  }
  getSidebarWrapperElement() {
    if (!this.sidebarWrapperElement) {
      this.sidebarWrapperElement = document.querySelector('.contentEditor-sidebar');
    }
    return this.sidebarWrapperElement;
  }
  getElementToUpdate() {
    if (!this.element) {
      this.element = document.querySelector('.editor-pane-container');
    }
    return this.element;
  }
  addClassName(classname) {
    const element = this.getElementToUpdate();
    if (element) {
      element.classList.add(classname);
    }
  }
  removeClassName(classname) {
    const element = this.getElementToUpdate();
    if (element) {
      element.classList.remove(classname);
    }
  }
  updateParentContainerClasses() {
    const {
      isBaymaxSidebarCollapsed
    } = this.props;
    if (!isBaymaxSidebarCollapsed) {
      this.addClassName('sidebar-is-expanded');
    } else {
      this.removeClassName('sidebar-is-expanded');
    }
  }
  renderCEUISidebarActivePanel() {
    const {
      children,
      activePanel,
      isOpen,
      isReadOnlyMode
    } = this.props;
    const sidebarContentClasses = classNames('inner-sliding-panel', SIDEBAR_PANELS.optimizer.includes(activePanel) && "scrollable", isReadOnlyMode && 'read-only');
    if (isOpen) {
      return /*#__PURE__*/_jsx("div", {
        className: sidebarContentClasses,
        children: children
      });
    }
    return null;
  }
  renderModuleListAndDesignSidebar() {
    const {
      children,
      activePanel,
      isOpen,
      onOpen,
      showAddTab,
      showDesignTab
    } = this.props;
    if (isOpen) {
      return /*#__PURE__*/_jsx("div", {
        className: "sidebar-content",
        children: /*#__PURE__*/_jsx(BaymaxSidebarTabsContainer, {
          addWidgetContent: null,
          selected: activePanel,
          onTabChange: onOpen,
          showAddTab: showAddTab,
          showDesignTab: showDesignTab,
          panelClassName: activePanel === SIDEBAR_PANELS.widgetList || activePanel === SIDEBAR_PANELS.design ? 'p-x-0 p-top-0 p-bottom-6 sidebar-scrollable-tab-panel' : undefined,
          children: children
        })
      });
    }
    return null;
  }
  renderSidebarIframeProxy() {
    const {
      isReadOnlyMode,
      sidebarIframeIsVisible,
      hasAnyContainerOrDndAreaToAddModules
    } = this.props;
    if (!hasAnyContainerOrDndAreaToAddModules || isReadOnlyMode || !sidebarIframeIsVisible) {
      return null;
    }
    return /*#__PURE__*/_jsx(PreloadedIframeProxy, {
      proxyTop: true,
      proxyDisplay: true,
      iframeWrapperElement: getSidebarIframeWrapperElement(),
      iframeStylesManager: SidebarIframeStylesManager
    });
  }
  renderAddModuleSidebar() {
    const {
      onOpen,
      showAddTab,
      showDesignTab,
      isReadOnlyMode
    } = this.props;
    if (isReadOnlyMode) {
      return null;
    }
    return /*#__PURE__*/_jsx(Fragment, {
      children: /*#__PURE__*/_jsx(BaymaxSidebarTabsContainer, {
        selected: 'addWidget',
        onTabChange: onOpen,
        showAddTab: showAddTab,
        showDesignTab: showDesignTab,
        panelClassName: "add-module-sidebar-tab",
        children: this.renderSidebarIframeProxy()
      })
    });
  }
  renderActivePanel() {
    const {
      activePanel
    } = this.props;
    const isDesignOrWidgetListSidebar = [SIDEBAR_PANELS.design, SIDEBAR_PANELS.widgetList].includes(activePanel);
    const isNonStandardSidebar = ![SIDEBAR_PANELS.addWidget, SIDEBAR_PANELS.design, SIDEBAR_PANELS.widgetList].includes(activePanel);
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [activePanel === SIDEBAR_PANELS.addWidget && this.renderAddModuleSidebar(), isDesignOrWidgetListSidebar && this.renderModuleListAndDesignSidebar(), isNonStandardSidebar && this.renderCEUISidebarActivePanel()]
    });
  }
  renderEditPanel() {
    const {
      activePanel,
      isReadOnlyMode,
      isOpen,
      children
    } = this.props;
    if (activePanel === SIDEBAR_PANELS.edit) {
      return /*#__PURE__*/_jsx(SlidingPanelWrapper, {
        isReadOnlyMode: isReadOnlyMode,
        isOpen: isOpen,
        transitionName: 'sidebar-panel-slide-in',
        transitionSpeed: 320,
        activePanel: activePanel,
        onClose: this.props.onClose,
        children: children
      }, "sliding-edit-panel");
    }
    return null;
  }
  renderFixedPanel() {
    const {
      activePanel
    } = this.props;
    if (activePanel === SIDEBAR_PANELS.edit) {
      return null;
    }
    return /*#__PURE__*/_jsx(FixedPanelWrapper, {
      transitionSpeed: 500,
      className: 'fixed-sidebar-panel',
      activePanel: activePanel,
      children: this.renderActivePanel()
    }, "fixed-sidebar-panel");
  }
  render() {
    const {
      currentTab,
      isOpen,
      activePanel,
      showDeviceToggle
    } = this.props;
    const hidden = currentTab && !currentTab.showSidebar;
    const sidebarClasses = classNames('expanding-sidebar clean-lines-styles', isOpen && 'is-expanded', hidden && 'is-hidden');
    return /*#__PURE__*/_jsx("div", {
      className: sidebarClasses,
      children: /*#__PURE__*/_jsxs("div", {
        className: "sidebar-content",
        id: "inpage-sidebar-container",
        children: [activePanel !== SIDEBAR_PANELS.optimizer && /*#__PURE__*/_jsx(SidebarHeader, {
          showDeviceToggle: showDeviceToggle
        }), /*#__PURE__*/_jsxs(TransitionGroup, {
          className: `sidebar-transition-group ${activePanel === SIDEBAR_PANELS.optimizer ? 'optimizer-panel-header scrollable-panel' : ''}`,
          component: "div",
          children: [this.renderFixedPanel(), this.renderEditPanel()]
        })]
      })
    });
  }
}
Sidebar.propTypes = {
  children: PropTypes.node.isRequired,
  sidebarIframeNeedsLoadingIndicator: PropTypes.bool.isRequired,
  activePanel: PropTypes.string,
  currentTab: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  showAddTab: PropTypes.bool.isRequired,
  showDesignTab: PropTypes.bool.isRequired,
  isReadOnlyMode: PropTypes.bool.isRequired,
  hasAnyContainerOrDndAreaToAddModules: PropTypes.bool.isRequired,
  showDeviceToggle: PropTypes.bool.isRequired,
  sidebarIframeIsVisible: PropTypes.bool.isRequired,
  isBaymaxSidebarCollapsed: PropTypes.bool.isRequired
};